import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, ExternalLink, FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import SelectMenu from "@/components/SelectMenu";

const KIND_OPTIONS = [
  { value: "all", label: "All intelligence" },
  { value: "watchlist_copilot", label: "Watchlist copilot" },
  { value: "daily_brief", label: "Daily briefs" },
  { value: "trade_of_day", label: "Trade of the Day" },
  { value: "analyst_answer", label: "Analyst answers" },
  { value: "contrarian", label: "Contrarian reports" },
  { value: "market_explain", label: "Market explains" },
  { value: "signal_narrative", label: "Signal narratives" },
  { value: "trader_summary", label: "Trader summaries" },
  { value: "watchlist_digest", label: "Digest notifications" },
];

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function targetLink(item) {
  if (!item.target_id) return null;
  if (item.target_kind === "market") return `/app/markets/${item.target_id}`;
  if (item.target_kind === "signal") return `/app/signals/${item.target_id}`;
  if (item.target_kind === "trader") return `/app/traders/${item.target_id}`;
  if (item.target_kind === "watchlist") return "/app/watchlists";
  return null;
}

function kindLabel(kind) {
  return (KIND_OPTIONS.find((o) => o.value === kind)?.label || kind || "History").replace(/s$/, "");
}

function renderText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "Structured history record";
  }
}

export default function History() {
  React.useEffect(() => { track("history_opened"); }, []);
  const [kind, setKind] = useState("all");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["intelligence-history", kind],
    queryFn: async () => (await api.get(`/intel/history?kind=${kind}&limit=75`)).data,
    refetchInterval: 60_000,
  });
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="history-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Intelligence memory</div>
          <h1 className="font-heading text-2xl font-bold text-zinc-100">History</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Saved AI briefs, watchlist copilot answers, daily picks, and digest notifications. This is the audit trail for why MarketTrace suggested something.
          </p>
        </div>
        <div className="w-full sm:w-64">
          <SelectMenu value={kind} onChange={setKind} options={KIND_OPTIONS} testid="select-history-kind" />
        </div>
      </div>

      {isLoading && <div className="text-xs text-zinc-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Loading history…</div>}
      {isError && <div className="text-xs text-rose-400">Could not load intelligence history.</div>}
      {!isLoading && items.length === 0 && (
        <div className="rounded-md border border-zinc-800 p-8 text-center text-xs text-zinc-500">
          No saved {kind === "all" ? "intelligence" : kindLabel(kind).toLowerCase()} yet. Generate a brief or run a watchlist copilot answer to populate this page.
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => {
          const link = targetLink(item);
          return (
            <article key={`${item.source}-${item.id}-${item.generated_at}`} className="rounded-md border border-zinc-800 bg-zinc-950/50 p-4" data-testid={`history-item-${item.kind}`}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded bg-zinc-800 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">
                      {kindLabel(item.kind)}
                    </span>
                    {item.fallback && <span className="text-[10px] text-amber-300">Fallback</span>}
                  </div>
                  <h2 className="mt-2 font-heading text-sm text-zinc-100 line-clamp-2">{item.title || kindLabel(item.kind)}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
                    <Clock size={11} /> <span>{fmtDate(item.generated_at)}</span>
                    {item.model && <><span>·</span><span className="font-mono-num">{item.model}</span></>}
                  </div>
                </div>
                {link && (
                  <Link to={link} className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-100 shrink-0" data-testid={`history-target-${item.id}`}>
                    Open source <ExternalLink size={10} />
                  </Link>
                )}
              </div>
              {item.text ? (
                <div className="mt-3 whitespace-pre-wrap text-xs leading-relaxed text-zinc-300 line-clamp-[14]">
                  {renderText(item.text)}
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500"><FileText size={12} /> Saved record has structured data but no text body.</div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
